import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { FileText, Download, ShieldCheck, ArrowRight, Mail, CheckCircle2, BookOpen } from 'lucide-react';
import { PartnerCTA } from '../components/PartnerCTA';
export function CataloguesPage() {
  const catalogues = [{
    title: 'Defence & Tactical Equipment',
    tag: 'Defence Sector',
    description: 'Comprehensive catalogue covering military-grade equipment manufactured to DEF STAN and NATO specifications. Includes detailed product specifications, material compositions, and compliance documentation.',
    includes: ['Uniforms & combat clothing', 'Load carriage systems', 'Ballistic protection', 'Camouflage netting solutions'],
    image: 'https://cdn.magicpatterns.com/uploads/cgpFBudHGosue3cH628Vuv/45161938-scaled.jpg',
    accent: 'cg-orange-600'
  }, {
    title: 'Police & Public Order',
    tag: 'Police Sector',
    description: 'Specialist catalogue for law enforcement procurement teams. Covers public order equipment, patrol gear, and protective clothing aligned to Home Office technical specifications.',
    includes: ['Public order shields', 'Control & restraint equipment', 'Patrol clothing & footwear', 'Protective equipment'],
    image: 'https://cdn.magicpatterns.com/uploads/njGsCZ1bVyNr1h2qALmbXq/banner-img.jpg',
    accent: 'navy-900'
  }, {
    title: 'Medical & Emergency Response',
    tag: 'Medical Sector',
    description: 'Product catalogue for healthcare providers and emergency services. Textile-based medical equipment and protective wear aligned to clinical operational and infection control standards.',
    includes: ['Medical protective wear', 'Emergency response equipment', 'Clinical textile products', 'Specialist medical accessories'],
    image: 'https://cdn.magicpatterns.com/uploads/cJmcybkRNgCksh1S8QiVuQ/What_we_do.jpg',
    accent: 'cg-orange-600'
  }, {
    title: 'Custodial & Restraint',
    tag: 'Prisons Sector',
    description: 'Dedicated catalogue for custodial and prison service procurement. Covers control and restraint equipment, cell entry gear, and specialist custodial products manufactured to Ministry of Justice specifications.',
    includes: ['Cell entry equipment', 'Control & restraint gear', 'Custodial clothing', 'Training equipment'],
    image: 'https://cdn.magicpatterns.com/uploads/sK8ZMn89EawgZpsPCJMPQz/Riot-and-Protection.jpg',
    accent: 'navy-900'
  }];
  return <>
      <PageHero label="Manufacturing" title="Our Catalogues" subtitle="Download our sector-specific product catalogues for detailed specifications, technical documentation, and ordering information across our full equipment range." backgroundImage="https://cdn.magicpatterns.com/uploads/rnUnJ6Ay6D84jn5vRbVj6B/Bags-and-backpacks.jpg" data-id="element-428" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Catalogues'
    }]} data-id="element-429" />

      {/* Intro Section */}
      <section className="bg-white py-20" data-id="element-430">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-431">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-432">
            <div className="lg:col-span-7" data-id="element-433">
              <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-3 block" data-id="element-434">
                Product Documentation
              </span>
              <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-435">
                Sector-Specific Catalogues
              </h2>
              <p className="text-lg text-steel-600 leading-relaxed mb-6" data-id="element-436">
                Each catalogue provides a comprehensive overview of our
                manufacturing capabilities within a specific sector. Catalogues
                include product specifications, material details, compliance
                information, and framework alignment where applicable.
              </p>
              <p className="text-steel-600 leading-relaxed" data-id="element-437">
                Catalogues are available on request to verified procurement
                professionals and organisational buyers. Submit a request below
                and our team will provide the relevant documentation for your
                sector.
              </p>
            </div>
            <div className="lg:col-span-5" data-id="element-438">
              <div className="bg-slate-50 border border-steel-200 border-l-4 border-l-cg-orange-600 p-8 rounded-sm" data-id="element-439">
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4" data-id="element-440">
                  What's Included
                </h3>
                <ul className="space-y-3" data-id="element-441">
                  {['Full product specifications and technical data', 'Material compositions and compliance standards', 'Framework lot alignment (BlueLight, YPO)', 'Ordering and procurement guidance', 'Contact details for category managers'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-442">
                      <CheckCircle2 className="w-4 h-4 text-cg-orange-600 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-443" />
                      <span className="text-sm text-steel-600" data-id="element-444">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catalogue Cards */}
      <section className="bg-slate-50 py-24 border-t border-steel-200" aria-labelledby="catalogues-heading" data-id="element-445">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-446">
          <h2 id="catalogues-heading" className="text-2xl font-bold text-navy-900 mb-12" data-id="element-447">
            Available Catalogues
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-448">
            {catalogues.map((cat, index) => <div key={cat.title} className="bg-white border border-steel-200 rounded-sm overflow-hidden shadow-sm hover:shadow-lg transition-shadow flex flex-col" data-id="element-449">
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden" data-id="element-450">
                  <img src={cat.image} alt={cat.title} className="w-full h-full object-cover" data-id="element-451" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/50 to-transparent" data-id="element-452" />
                  <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-453">
                    <span className="text-xs font-bold uppercase tracking-widest text-cg-orange-600 block mb-2" data-id="element-454">
                      {cat.tag}
                    </span>
                    <h3 className="text-xl font-bold text-white" data-id="element-455">
                      {cat.title}
                    </h3>
                  </div>
                  {/* Document badge */}
                  <div className="absolute top-4 right-4" data-id="element-456">
                    <div className="w-10 h-10 bg-white/90 rounded-sm flex items-center justify-center shadow-sm" data-id="element-457">
                      <BookOpen className="w-5 h-5 text-navy-900" aria-hidden="true" data-id="element-458" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col flex-1" data-id="element-459">
                  <p className="text-sm text-steel-600 leading-relaxed mb-6" data-id="element-460">
                    {cat.description}
                  </p>

                  <div className="mb-8 flex-1" data-id="element-461">
                    <h4 className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-3" data-id="element-462">
                      Catalogue Contents
                    </h4>
                    <ul className="space-y-2" data-id="element-463">
                      {cat.includes.map((item, i) => <li key={i} className="flex items-center gap-2 text-sm text-steel-600" data-id="element-464">
                          <ArrowRight className="w-3 h-3 text-cg-orange-600 flex-shrink-0" aria-hidden="true" data-id="element-465" />
                          {item}
                        </li>)}
                    </ul>
                  </div>

                  <div className="flex items-center gap-3 pt-6 border-t border-steel-100" data-id="element-466">
                    <a href="#request-catalogue" className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-bold uppercase tracking-wider text-sm rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cg-orange-600 focus-visible:ring-offset-2 ${index % 2 === 0 ? 'bg-cg-orange-600 text-white hover:bg-cg-orange-500' : 'bg-navy-900 text-white hover:bg-cg-orange-600'}`} data-id="element-467">
                      <Download className="w-4 h-4" aria-hidden="true" data-id="element-468" />
                      Request Catalogue
                    </a>
                    <span className="text-[10px] font-bold text-steel-400 uppercase tracking-wider" data-id="element-469">
                      PDF
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* How to Request — Step Process */}
      <section className="bg-white py-20 border-t border-steel-200" aria-labelledby="how-to-request-heading" data-id="element-470">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-471">
          <h2 id="how-to-request-heading" className="text-xl font-bold text-navy-900 mb-10 text-center" data-id="element-472">
            How to Request a Catalogue
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto" data-id="element-473">
            {[{
            step: '01',
            title: 'Select Your Sector',
            desc: 'Identify the catalogue relevant to your procurement requirement and operational sector.'
          }, {
            step: '02',
            title: 'Contact Our Team',
            desc: 'Reach out via the contact form or email with your organisation details and sector of interest.'
          }, {
            step: '03',
            title: 'Receive Documentation',
            desc: 'Our team will verify your request and send the relevant catalogue and supporting materials.'
          }].map((item, i) => <div key={i} className="text-center" data-id="element-474">
                <div className="w-14 h-14 bg-navy-900 text-white font-bold text-lg rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-475">
                  {item.step}
                </div>
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-3" data-id="element-476">
                  {item.title}
                </h3>
                <p className="text-sm text-steel-600 leading-relaxed" data-id="element-477">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Request CTA — Dark Treatment */}
      

      {/* Partner CTA */}
      <PartnerCTA data-id="element-478" />
    </>;
}